
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    rto_name: string;
    renewal_date: string;
    expiry_date: string;
    amount: string;
    rc_document: FileList;
    remarks?: string;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function AddRcRenewal() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    const selectedBranch = watch("branch_id");
    const watchedRenewalDate = watch("renewal_date");
    const watchedExpiryDate = watch("expiry_date");

    // Fetch Branches on Mount
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const data: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    // Fetch Vehicles when Branch changes
    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) {
                setVehicles([]);
                return;
            }
            try {
                const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                if (Array.isArray(response)) {
                    const activeVehicles = response.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (response.data) {
                    const activeVehicles = Array.isArray(response.data) ? response.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                } else {
                    setVehicles([]);
                }
            } catch (error) {
                console.error(error);
                setVehicles([]);
            }
        };

        fetchVehicles();
        setValue("vehicle_id", "");
    }, [selectedBranch, setValue]);

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            let docName = "";
            if (data.rc_document && data.rc_document.length > 0) {
                const file = data.rc_document[0];
                if (file.size > 2 * 1024 * 1024) {
                    toast.error("File size must be less than 2MB");
                    setLoading(false);
                    return;
                }
                docName = await uploadFile(file);
            }

            const payload = {
                ...data,
                rc_document: docName,
            };

            await apiPost("rc_renewal/store", payload);
            toast.success("RC Renewal added successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/rc_renewal`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error adding RC renewal");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add RC Renewal" parent="Renewal" title="Add RC Renewal" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <H5>Renewal Details</H5>
                            <hr />
                            <Row className="g-3">
                                <Col md={4}>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="branch_id"
                                        control={control}
                                        rules={{ required: "Branch is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                    <option value="">Select Branch</option>
                                                    {branches.map((b: any) => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Vehicle <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="vehicle_id"
                                        control={control}
                                        rules={{ required: "Vehicle is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.vehicle_id} disabled={!selectedBranch}>
                                                    <option value="">Select Vehicle</option>
                                                    {vehicles.map((v: any) => (
                                                        <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>RTO / Authority Name <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="rto_name"
                                        control={control}
                                        rules={{ required: "Authority Name is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="e.g. RTA Dubai" invalid={!!errors.rto_name} autoComplete="off" />
                                                <FormFeedback>{errors.rto_name?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Start Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="renewal_date"
                                        control={control}
                                        rules={{ required: "Start Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.renewal_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedExpiryDate && d && new Date(d) > new Date(watchedExpiryDate)) {
                                                            toast.error("Start date cannot be after Expiry date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className="d-block">{errors.renewal_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Expiry Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="expiry_date"
                                        control={control}
                                        rules={{ required: "Expiry Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.expiry_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedRenewalDate && d && new Date(d) < new Date(watchedRenewalDate)) {
                                                            toast.error("Expiry date cannot be before Start date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className="d-block">{errors.expiry_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Renewal Amount <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="amount"
                                        control={control}
                                        rules={{ required: "Amount is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="number" {...field} placeholder="Amount" invalid={!!errors.amount} autoComplete="off" />
                                                <FormFeedback>{errors.amount?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>RC Document <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="rc_document"
                                        control={control}
                                        rules={{ required: "RC Document is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    accept="image/*,.pdf"
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                            const file = e.target.files[0];
                                                            if (file.size > 2 * 1024 * 1024) {
                                                                toast.error("File size must be less than 2MB");
                                                                e.target.value = "";
                                                                onChange(null);
                                                                return;
                                                            }
                                                        }
                                                        onChange(e.target.files);
                                                    }}
                                                    invalid={!!errors.rc_document}
                                                    autoComplete="off"
                                                />
                                                <FormFeedback>{errors.rc_document?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={12}>
                                    <Label>Remarks</Label>
                                    <Controller
                                        name="remarks"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input type="textarea" {...field} placeholder="Any remarks..." />
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <div className="mt-4">
                                <Btn color="primary" disabled={loading} type="submit">
                                    {loading ? <>Submitting <Spinner size="sm" /></> : "Submit"}
                                </Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
